class UIHandler {
  private draggedClone: HTMLElement | null = null;
  private draggedPlacedFilter: HTMLElement | null = null; // Track if dragging a placed filter
  private fluidContainer: HTMLElement | null = null;
  private isDragging = false;
  private hasDragged = false; // Track if mouse has moved during drag
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;
  private initialMouseX = 0; // Track initial mouse position
  private initialMouseY = 0;
  private placedFilters: Set<string> = new Set(); // Track placed filter IDs
  private placedFilterElements: Map<string, HTMLElement> = new Map(); // Track wrapper elements
  private lastPlacedFilterId: string | null = null; // Track the last placed filter
  private readonly MIN_DROP_DISTANCE = 20; // Minimum pixels from origin to drop
  private isDrawerOpen: boolean = false; // Track drawer open/closed state
  private filterDrawers: NodeListOf<HTMLElement> | null = null; // Track drawer elements
  private toggleButton: HTMLElement | null = null; // Track toggle button
  private isDraggingModule: boolean = false; // Track if dragging the entire module
  private moduleContainer: HTMLElement | null = null; // The .addFilter container
  private filterTooltip: HTMLElement | null = null; // The filter button tooltip

  constructor() {
    this.fluidContainer = document.querySelector(".category-container");
    console.log("Container found:", this.fluidContainer);
    this.init();
  }

  /**
   * Get the ID of the currently active filter
   */
  getActiveFilterId(): string | null {
    return this.lastPlacedFilterId;
  }

  /**
   * Get positions of all placed category filters
   * Returns array of {categoryId, x, y}
   */
  getPlacedFilterPositions(): Array<{
    categoryId: number;
    x: number;
    y: number;
  }> {
    const positions: Array<{ categoryId: number; x: number; y: number }> = [];

    this.placedFilterElements.forEach((element, filterId) => {
      const x = parseFloat(element.style.left);
      const y = parseFloat(element.style.top);

      if (!isNaN(x) && !isNaN(y)) {
        positions.push({
          categoryId: parseInt(filterId),
          x: x,
          y: y,
        });
      }
    });

    return positions;
  }

  private init(): void {
    // Find toggle button and drawer elements
    this.toggleButton = document.querySelector(".addFilterButton");
    this.filterDrawers = document.querySelectorAll(".filterDrawer");
    this.moduleContainer = document.querySelector(".addFilter");
    this.filterTooltip = document.querySelector(".filter-tooltip");

    // Add toggle button listener
    if (this.toggleButton) {
      this.toggleButton.addEventListener("click", (e) => {
        // Only toggle if not dragged
        if (!this.hasDragged) {
          this.toggleDrawer();
        }
      });

      // Add mousedown handler for dragging (always enabled)
      this.toggleButton.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.startModuleDrag(e as PointerEvent);
      });
    }

    // Find all draggable category buttons
    const categoryButtons = document.querySelectorAll(
      ".categoryButton.draggable"
    );

    categoryButtons.forEach((button) => {
      button.addEventListener("pointerdown", (e) =>
        this.handleMouseDown(e as PointerEvent)
      );
    });

    // Global pointer events for dragging (works for both mouse and touch)
    document.addEventListener("pointermove", (e) =>
      this.handleMouseMove(e as PointerEvent)
    );
    document.addEventListener("pointerup", (e) =>
      this.handleMouseUp(e as PointerEvent)
    );

    // Reset module position on window resize
    window.addEventListener("resize", () => this.resetModulePosition());

    // Initialize tooltip position
    this.updateFilterTooltipPosition();
  }

  private resetModulePosition(): void {
    if (this.moduleContainer) {
      this.moduleContainer.style.left = "25%";
      this.moduleContainer.style.bottom = "25%";
      this.updateFilterTooltipPosition();
    }
  }

  private toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;

    if (this.filterDrawers) {
      this.filterDrawers.forEach((drawer) => {
        if (this.isDrawerOpen) {
          drawer.classList.add("open");
        } else {
          drawer.classList.remove("open");
        }
      });
    }

    // Rotate toggle button
    if (this.toggleButton) {
      if (this.isDrawerOpen) {
        this.toggleButton.classList.add("open");
      } else {
        this.toggleButton.classList.remove("open");
      }
    }
  }

  private startModuleDrag(e: PointerEvent): void {
    this.isDraggingModule = true;
    this.hasDragged = false; // Reset drag flag
    this.currentX = e.clientX;
    this.currentY = e.clientY;
    this.initialMouseX = e.clientX;
    this.initialMouseY = e.clientY;

    if (this.moduleContainer) {
      this.moduleContainer.style.cursor = "grabbing";
    }

    // Update tooltip position
    this.updateFilterTooltipPosition();
  }

  private updateButtonState(filterId: string): void {
    const buttons = document.querySelectorAll(
      `.filterDrawer .categoryButton[data-filter="${filterId}"]`
    );
    const isPlaced = this.placedFilters.has(filterId);

    console.log(
      `Updating button state for filter ${filterId}: isPlaced = ${isPlaced}`
    );

    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        if (isPlaced) {
          button.classList.add("disabled");
        } else {
          button.classList.remove("disabled");
          button.style.opacity = "1"; // Reset opacity when re-enabled
        }
      }
    });
  }

  private handleMouseDown(e: PointerEvent): void {
    const target = e.currentTarget as HTMLElement;

    // Check if this is a placed filter wrapper being dragged
    if (
      target.classList.contains("placed-filter-wrapper") ||
      this.draggedPlacedFilter?.classList.contains("placed-filter-wrapper")
    ) {
      // Already handled in placeOnCanvas
      return;
    }

    // Check if this is a placed filter being dragged (legacy, shouldn't happen now)
    if (target.classList.contains("placed-filter")) {
      e.preventDefault();
      e.stopPropagation();

      this.draggedPlacedFilter = target;
      this.isDragging = true;
      this.hasDragged = false; // Reset drag flag

      // Get current position
      const rect = target.getBoundingClientRect();
      this.currentX = e.clientX;
      this.currentY = e.clientY;

      // Add dragging visual feedback
      target.style.cursor = "grabbing";
      target.style.opacity = "0.8";
      target.style.zIndex = "1000";

      return;
    }

    // Check if button is disabled
    if (target.classList.contains("disabled")) {
      return;
    }

    e.preventDefault();

    // Hide original button immediately when drag starts
    target.style.opacity = "0";

    // Create a clone of the button
    this.draggedClone = target.cloneNode(true) as HTMLElement;
    this.draggedClone.classList.add("dragging-clone");

    // Remove tooltip from clone
    const tooltip = this.draggedClone.querySelector(".custom-tooltip");
    if (tooltip) {
      tooltip.remove();
    }

    // Position it absolutely
    this.draggedClone.style.position = "fixed";
    this.draggedClone.style.pointerEvents = "none";
    this.draggedClone.style.zIndex = "1000";
    this.draggedClone.style.cursor = "grabbing";
    this.draggedClone.style.opacity = "1"; // Ensure clone is visible

    // Get the initial position
    const rect = target.getBoundingClientRect();
    this.startX = rect.left;
    this.startY = rect.top;
    this.currentX = e.clientX;
    this.currentY = e.clientY;
    this.initialMouseX = e.clientX;
    this.initialMouseY = e.clientY;

    // Position the clone at the button's location
    this.draggedClone.style.left = `${this.startX}px`;
    this.draggedClone.style.top = `${this.startY}px`;

    document.body.appendChild(this.draggedClone);
    this.isDragging = true;

    // Add visual feedback to original button
  }

  private handleMouseMove(e: PointerEvent): void {
    // Handle module dragging
    if (this.isDraggingModule && this.moduleContainer) {
      const deltaX = e.clientX - this.currentX;
      const deltaY = e.clientY - this.currentY;

      // Mark that we've moved during this drag
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        this.hasDragged = true;
      }

      this.currentX = e.clientX;
      this.currentY = e.clientY;

      // Get current position
      const currentLeft = parseFloat(this.moduleContainer.style.left) || 25; // Default 20%
      const currentBottom = parseFloat(this.moduleContainer.style.bottom) || 25; // Default 20%

      // Calculate new position in viewport percentage
      const newLeft =
        (((currentLeft / 100) * window.innerWidth + deltaX) /
          window.innerWidth) *
        100;
      const newBottom =
        (((currentBottom / 100) * window.innerHeight - deltaY) /
          window.innerHeight) *
        100;

      this.moduleContainer.style.left = `${newLeft}%`;
      this.moduleContainer.style.bottom = `${newBottom}%`;

      // Update tooltip position
      this.updateFilterTooltipPosition();
      return;
    }

    if (!this.isDragging) return;

    const deltaX = e.clientX - this.currentX;
    const deltaY = e.clientY - this.currentY;

    // Mark that we've moved during this drag
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      this.hasDragged = true;
    }

    this.currentX = e.clientX;
    this.currentY = e.clientY;

    // If dragging a placed filter, move it directly
    if (this.draggedPlacedFilter) {
      const currentLeft = parseFloat(this.draggedPlacedFilter.style.left);
      const currentTop = parseFloat(this.draggedPlacedFilter.style.top);

      this.draggedPlacedFilter.style.left = `${currentLeft + deltaX}px`;
      this.draggedPlacedFilter.style.top = `${currentTop + deltaY}px`;
      return;
    }

    // Otherwise, move the clone
    if (this.draggedClone) {
      const currentLeft = parseFloat(this.draggedClone.style.left);
      const currentTop = parseFloat(this.draggedClone.style.top);

      this.draggedClone.style.left = `${currentLeft + deltaX}px`;
      this.draggedClone.style.top = `${currentTop + deltaY}px`;
    }
  }

  private handleMouseUp(e: PointerEvent): void {
    // Handle module drag end
    if (this.isDraggingModule) {
      this.isDraggingModule = false;
      if (this.moduleContainer) {
        this.moduleContainer.style.cursor = "";
      }
      return;
    }

    if (!this.isDragging) return;

    // If we were dragging a placed filter
    if (this.draggedPlacedFilter) {
      // Always make the dragged/clicked filter active (whether dragged or clicked)
      const clickedFilterId =
        this.draggedPlacedFilter.getAttribute("data-filter");
      if (clickedFilterId) {
        // Remove active class from previously active filter
        if (
          this.lastPlacedFilterId &&
          this.lastPlacedFilterId !== clickedFilterId
        ) {
          const prevWrapper = this.placedFilterElements.get(
            this.lastPlacedFilterId
          );
          if (prevWrapper) {
            const prevFilter = prevWrapper.querySelector(".placed-filter");
            prevFilter?.classList.remove("active");
          }
        }

        // Add active class to clicked filter
        const clickedFilter =
          this.draggedPlacedFilter.querySelector(".placed-filter") ||
          this.draggedPlacedFilter;
        clickedFilter.classList.add("active");
        this.lastPlacedFilterId = clickedFilterId;
      }

      this.draggedPlacedFilter.style.cursor = "grab";
      this.draggedPlacedFilter.style.opacity = "1";
      this.draggedPlacedFilter.style.zIndex = "15";
      this.draggedPlacedFilter = null;
      this.isDragging = false;
      this.hasDragged = false;
      return;
    }

    if (!this.draggedClone) return;

    // Store the filter ID before any removal
    const filterId = this.draggedClone.getAttribute("data-filter");

    // Calculate distance from origin
    const dragDistance = Math.sqrt(
      Math.pow(e.clientX - this.initialMouseX, 2) +
        Math.pow(e.clientY - this.initialMouseY, 2)
    );

    // If dragged less than minimum distance, cancel drop
    if (dragDistance < this.MIN_DROP_DISTANCE) {
      console.log(
        `Drop cancelled: only moved ${dragDistance.toFixed(0)}px (minimum ${this.MIN_DROP_DISTANCE}px)`
      );
      this.draggedClone.remove();

      // Reset original button opacity
      if (filterId && !this.placedFilters.has(filterId)) {
        const originalButtons = document.querySelectorAll(
          `.filterDrawer .categoryButton[data-filter="${filterId}"]`
        );
        originalButtons.forEach((button) => {
          if (
            button instanceof HTMLElement &&
            !button.classList.contains("disabled")
          ) {
            button.style.opacity = "1";
          }
        });
      }

      this.draggedClone = null;
      this.isDragging = false;
      return;
    }

    // Check if dropped on the canvas/fluid container
    // Get fresh bounds each time in case container has grown
    const fluidContainerRect = this.fluidContainer?.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY + window.scrollY;
    if (fluidContainerRect) {
      // Adjust Y coordinate for scroll position
      const scrollAdjustedTop = fluidContainerRect.top + window.scrollY;
      const scrollAdjustedBottom = fluidContainerRect.bottom + window.scrollY;

      const isOverCanvas =
        x >= fluidContainerRect.left &&
        x <= fluidContainerRect.right &&
        y >= scrollAdjustedTop &&
        y <= scrollAdjustedBottom;

      if (isOverCanvas) {
        // Use client coordinates directly since fluid-container is fixed position
        this.placeOnCanvas(this.draggedClone, x, y);
      } else {
        // Not dropped on canvas, remove clone
        this.draggedClone.remove();

        // Reset original button opacity (only if not already placed)
        if (filterId && !this.placedFilters.has(filterId)) {
          const originalButtons = document.querySelectorAll(
            `.filterDrawer .categoryButton[data-filter="${filterId}"]`
          );
          originalButtons.forEach((button) => {
            if (
              button instanceof HTMLElement &&
              !button.classList.contains("disabled")
            ) {
              button.style.opacity = "1";
            }
          });
        }
      }
    } else {
      this.draggedClone.remove();

      // Reset original button opacity (only if not already placed)
      if (filterId && !this.placedFilters.has(filterId)) {
        const originalButtons = document.querySelectorAll(
          `.filterDrawer .categoryButton[data-filter="${filterId}"]`
        );
        originalButtons.forEach((button) => {
          if (
            button instanceof HTMLElement &&
            !button.classList.contains("disabled")
          ) {
            button.style.opacity = "1";
          }
        });
      }
    }

    this.draggedClone = null;
    this.isDragging = false;
  }

  private placeOnCanvas(clone: HTMLElement, x: number, y: number): void {
    if (!this.fluidContainer) return;

    const filterId = clone.getAttribute("data-filter") || "";

    // Remove from body first
    clone.remove();

    // Create a wrapper for the filter with close button
    const wrapper = document.createElement("div");
    wrapper.className = "placed-filter-wrapper";
    wrapper.setAttribute("data-filter", filterId);
    wrapper.style.position = "absolute";
    wrapper.style.left = `${x}px`;
    wrapper.style.top = `${y}px`;
    wrapper.style.transform = "translate(-50%, -50%)";
    wrapper.style.pointerEvents = "auto";
    wrapper.style.zIndex = "15";
    wrapper.style.display = "flex";
    wrapper.style.alignItems = "center";
    wrapper.style.gap = "0.25rem";

    // Create a new element to avoid any lingering event listeners
    const placedFilter = document.createElement("div");
    placedFilter.className = "categoryButton placed-filter";
    placedFilter.setAttribute("data-filter", filterId);
    placedFilter.textContent = clone.textContent;
    placedFilter.style.cursor = "grab";
    placedFilter.style.margin = "0";

    // Create close button
    const closeButton = document.createElement("button");
    closeButton.className = "close-filter-button";
    closeButton.innerHTML = "×";
    closeButton.style.cursor = "pointer";
    closeButton.style.background = "rgba(0, 0, 0, 0.8)";
    closeButton.style.color = "white";
    closeButton.style.border = "none";
    closeButton.style.borderRadius = "50%";
    closeButton.style.width = "1.25rem";
    closeButton.style.height = "1.25rem";
    closeButton.style.display = "flex";
    closeButton.style.alignItems = "center";
    closeButton.style.justifyContent = "center";
    closeButton.style.fontSize = "1rem";
    closeButton.style.lineHeight = "1";
    closeButton.style.padding = "0";
    closeButton.style.flexShrink = "0";

    wrapper.appendChild(placedFilter);
    wrapper.appendChild(closeButton);

    // Add to fluid container
    this.fluidContainer.appendChild(wrapper);

    // Remove active class from previously active filter
    if (this.lastPlacedFilterId) {
      const prevWrapper = this.placedFilterElements.get(
        this.lastPlacedFilterId
      );
      if (prevWrapper) {
        const prevFilter = prevWrapper.querySelector(".placed-filter");
        prevFilter?.classList.remove("active");
      }
    }

    // Mark this filter as placed and update button state
    this.placedFilters.add(filterId);
    this.placedFilterElements.set(filterId, wrapper);
    this.lastPlacedFilterId = filterId;
    placedFilter.classList.add("active"); // Mark as active
    this.updateButtonState(filterId);

    // Add drag event listener for repositioning on the wrapper (so entire area is draggable)
    const startDrag = (e: PointerEvent) => {
      // Don't start drag if clicking directly on the close button
      const target = e.target as HTMLElement;
      if (
        target.classList.contains("close-filter-button") ||
        target.closest(".close-filter-button")
      ) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();
      // Set the wrapper as the dragged element
      this.draggedPlacedFilter = wrapper;
      this.isDragging = true;
      this.hasDragged = false; // Reset drag flag
      this.currentX = e.clientX;
      this.currentY = e.clientY;
      wrapper.style.cursor = "grabbing";
      wrapper.style.opacity = "0.8";
      wrapper.style.zIndex = "1000";
    };

    // Add listeners to both wrapper and filter for better touch handling
    wrapper.addEventListener("pointerdown", startDrag);

    // Close button handler - only click, no pointerdown to avoid conflicts
    closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset opacity before removing
      wrapper.style.opacity = "1";
      wrapper.remove();

      // Remove from placed filters and re-enable button
      this.placedFilters.delete(filterId);
      this.placedFilterElements.delete(filterId);

      // If this was the last placed filter, clear the reference
      if (this.lastPlacedFilterId === filterId) {
        this.lastPlacedFilterId = null;
      }

      this.updateButtonState(filterId);

      console.log("Filter removed from canvas");
    });

    console.log("Filter successfully placed:", placedFilter.textContent);
  }

  private updateFilterTooltipPosition(): void {
    if (!this.filterTooltip || !this.moduleContainer) return;

    const rect = this.moduleContainer.getBoundingClientRect();
    this.filterTooltip.style.left = `${rect.left}px`;
    this.filterTooltip.style.bottom = `${window.innerHeight - rect.top + 30}px`;
  }
}

export default UIHandler;
